/**
 * Literais das permissions de contato (`app/permissions/permissions.py::ContactsPermissions`).
 *
 * Existe como const porque o contract test de `literals.test.ts` só alcança
 * CONSTS: um `useHasPermission('contacts:history:read')` inline não é coberto por
 * nada, e `check.ts` faz `includes()` exato — não há wildcard `contacts:*`, então
 * literal errado é fail-closed e mudo.
 *
 * Presets do back (`sub_roles/defaults.py::_CICLO_ATENDIMENTO`): `atendente` e
 * `owner` têm `BLOCKED_READ`. `DELETED_READ`, `HISTORY_READ`, `UNREACHABLE_CLEAR`, `SUMMARY_READ`, `TIMELINE_READ` e `GALLERY_READ` estão FORA de todo
 * preset — concessão em runtime, por tenant.
 */
export const CONTACTS_PERMISSIONS = {
  READ: 'contacts:read',
  /** `PATCH /contacts/{id}/block?block=`. **Desbloquear exige `BLOCKED_READ` junto** —
   *  o gate de estado do back (`_ensure_contact_mutable`) recusa mutar contato num
   *  estado que o chamador não pode ver, e no unblock o contato JÁ está bloqueado.
   *  O gate é por ESTADO, não por operação: só o PRIMEIRO bloqueio dispensa
   *  `BLOCKED_READ`. Rebloquear/retentar (`?block=true` sobre contato já bloqueado)
   *  exige as duas — ver `permissions/contacts.md`. */
  BLOCK: 'contacts:block',
  /** Também é a permission do `POST /contacts/{id}/restore`. */
  DELETE: 'contacts:delete',
  HISTORY_READ: 'contacts:history:read',
  BLOCKED_READ: 'contacts:blocked:read',
  DELETED_READ: 'contacts:deleted:read',
  /**
   * `POST /contacts/{id}/clear_unreachable`. **Runtime-only**: fora de TODO preset
   * do back (nem `atendente` nem `owner` a concedem) — o botão **nasce 403** até o
   * grant existir por tenant, e isso é ESTADO, não bug.
   *
   * **Não tem irmã de leitura.** Não existe `contacts:unreachable:read`: o eixo
   * `?unreachable=` e o rótulo do estado pedem só `contacts:read`. Bloqueado,
   * e deletado escondem decisão humana; `unreachable` é fato da Meta.
   * O gate de ESTADO das mutações continua valendo por cima: desmarcar contato
   * bloqueado exige também a `BLOCKED_READ`.
   */
  UNREACHABLE_CLEAR: 'contacts:unreachable:clear',
  /**
   * `GET /contacts/{id}/summary` (aba Resumo da ficha). **Runtime-only**: fora de TODO
   * preset do back (`sub_roles/defaults.py` não muda) — até o user decidir os presets,
   * só quem recebe tudo em runtime (`dstorres`) vê a aba.
   */
  SUMMARY_READ: 'contacts:summary:read',
  /** `GET /contacts/{id}/timeline` (aba Linha do tempo). Runtime-only, como `SUMMARY_READ`. */
  TIMELINE_READ: 'contacts:timeline:read',
  /**
   * `GET /contacts/{id}/gallery` (aba Galeria). Runtime-only. **Sozinha não basta**: a rota
   * exige ESTA **e** `protocols:messages:read` (`PROTOCOLS_PERMISSIONS.MESSAGES_READ`) — ela
   * entrega conteúdo de mensagem. O gate da aba pede as duas.
   */
  GALLERY_READ: 'contacts:gallery:read',
} as const
